import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Plus,
  Receipt,
  Calculator,
  Calendar,
  Buildings,
  Money,
  Bank,
  ArrowDownRight,
  FilePdf,
  Eye,
  CheckCircle,
  Clock,
  MagnifyingGlass,
  Paperclip,
  Warning,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { Modal } from '@/components/Modal'
import { supabase } from '@/lib/supabase'
import { hoyGT } from '../lib/fechas'

/**
 * Módulo Gastos — registrar facturas de proveedores con retención ISR automática.
 *
 * Reglas confirmadas con el Contador General Carlitos (2026-06-26):
 *
 *   ISR:
 *     - Cooperativa exenta de IVA (asociados) → IVA va incluido en el gasto
 *     - Si dice "SUJETO A RETENCIÓN DEFINITIVA" Y monto > Q2,500 → retiene ISR
 *     - Cálculo sobre monto base (monto_total / 1.12):
 *       · 5% hasta Q30,000 base
 *       · 7% sobre el excedente arriba de Q30,000
 *     - Si dice "SUJETO A PAGOS TRIMESTRALES" (Régimen Utilidades) → NO retiene
 *     - Si es Pequeño Contribuyente → NO retiene
 *
 * Toda la lógica de ISR vive en la función SQL calcular_isr_dual(monto, sujeto).
 *
 * Asiento contable generado por registrar_gasto(jsonb):
 *   DEBE  <cuenta_gasto>      monto_total
 *   HABER  3051010602 ISR     isr_retenido (si aplica)
 *   HABER  <cuenta_pago>      monto_pagar (banco o caja)
 */

type Proveedor = {
  id: string
  nit: string | null
  nombre: string
  tipo: string | null
  regimen_tributario: 'DEFINITIVO' | 'PAGOS_TRIMESTRALES' | 'PEQUEÑO_CONTRIBUYENTE' | 'OTROS' | null
  banco: string | null
  cuenta_bancaria: string | null
  activo: boolean
}

type CuentaBancariaPropia = {
  id: string
  banco: string
  numero_cuenta: string
  alias: string | null
  tipo: string
  moneda: string
  activa: boolean
}

type CategoriaGasto =
  | 'NOMINA' | 'PRESTACIONES' | 'ALQUILER' | 'SERVICIOS' | 'SUMINISTROS'
  | 'PROFESIONALES' | 'MARKETING' | 'COMISIONES_BANCO' | 'DEPRECIACION'
  | 'IMPUESTOS' | 'CAPACITACION' | 'TRANSPORTE' | 'MANTENIMIENTO'
  | 'SEGUROS' | 'OTROS'

const CATEGORIAS: { value: CategoriaGasto; label: string }[] = [
  { value: 'PROFESIONALES',    label: 'Honorarios profesionales' },
  { value: 'ALQUILER',         label: 'Arrendamientos' },
  { value: 'NOMINA',           label: 'Funcionarios y Empleados' },
  { value: 'PRESTACIONES',     label: 'Prestaciones laborales' },
  { value: 'TRANSPORTE',       label: 'Transporte y viáticos' },
  { value: 'MARKETING',        label: 'Mercadeo y publicidad' },
  { value: 'SUMINISTROS',      label: 'Papelería y útiles' },
  { value: 'SERVICIOS',        label: 'Servicios básicos' },
  { value: 'MANTENIMIENTO',    label: 'Mantenimiento' },
  { value: 'SEGUROS',          label: 'Seguros' },
  { value: 'CAPACITACION',     label: 'Capacitación' },
  { value: 'DEPRECIACION',     label: 'Depreciaciones' },
  { value: 'IMPUESTOS',        label: 'Impuestos y multas' },
  { value: 'COMISIONES_BANCO', label: 'Comisiones bancarias' },
  { value: 'OTROS',            label: 'Otros' },
]

type FormaPago = 'efectivo' | 'transferencia' | 'cheque' | 'deposito' | 'pendiente'

const FORMAS_PAGO: { value: FormaPago; label: string }[] = [
  { value: 'efectivo',      label: 'Efectivo (caja)' },
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'deposito',      label: 'Depósito bancario' },
  { value: 'pendiente',     label: 'Pendiente de pago' },
]

type GastoOperativo = {
  id: string
  fecha: string
  categoria: CategoriaGasto
  cuenta_contable: string
  proveedor_id: string | null
  proveedor_texto: string | null
  descripcion: string
  monto_neto: number
  isr_retenido: number
  monto_total: number
  forma_pago: FormaPago | null
  cuenta_bancaria_propia_id: string | null
  factura_serie: string | null
  factura_numero: string | null
  factura_url: string | null
  comprobante_url: string | null
  constancia_sat_url: string | null
  asiento_id: string | null
  estado: string
  notas: string | null
  created_at: string
}

type IsrCalc = {
  monto_total: number
  monto_base: number
  isr_retenido: number
  monto_pagar: number
  sujeto_retencion: boolean
  tramos: Array<{ descripcion: string; tasa: number; monto: number }>
}

const fmtQ = (n: number) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(n)

const fmtDate = (s: string) =>
  new Date(s + 'T00:00:00').toLocaleDateString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
  })

export function NeoCoreGastosPage() {
  const [rows, setRows] = useState<GastoOperativo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'pagados' | 'pendientes'>('todos')
  const [filtroMes, setFiltroMes] = useState<string>('todos') // YYYY-MM o 'todos'
  const [selected, setSelected] = useState<GastoOperativo | null>(null)

  const reload = () => {
    setLoading(true)
    setError(null)
    supabase
      .from('gasto_operativo')
      .select('*')
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRows((data ?? []) as GastoOperativo[])
        setLoading(false)
      })
  }
  useEffect(reload, [])

  // KPIs de la lista (sobre TODOS los gastos, no los filtrados)
  const stats = useMemo(() => {
    const totalGastos = rows.reduce((s, r) => s + (r.monto_total ?? 0), 0)
    const totalIsr = rows.reduce((s, r) => s + (r.isr_retenido ?? 0), 0)
    const esteMes = rows.filter((r) => {
      const d = new Date(r.fecha + 'T00:00:00')
      const now = new Date()
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    })
    const pendientes = rows.filter((r) => r.estado === 'registrado')
    return {
      total: rows.length,
      totalGastos,
      totalIsr,
      esteMes: esteMes.length,
      esteMesMonto: esteMes.reduce((s, r) => s + (r.monto_total ?? 0), 0),
      pendientes: pendientes.length,
      pendientesMonto: pendientes.reduce((s, r) => s + (r.monto_total ?? 0), 0),
    }
  }, [rows])

  // Meses únicos para el filtro
  const mesesDisponibles = useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) set.add(r.fecha.slice(0, 7))
    return Array.from(set).sort().reverse()
  }, [rows])

  // Aplicar filtros
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (filtroEstado === 'pagados' && r.estado !== 'pagado') return false
      if (filtroEstado === 'pendientes' && r.estado === 'pagado') return false
      if (filtroMes !== 'todos' && !r.fecha.startsWith(filtroMes)) return false
      if (!q) return true
      return [r.proveedor_texto, r.descripcion, r.factura_numero, r.factura_serie].some(
        (v) => v && String(v).toLowerCase().includes(q),
      )
    })
  }, [rows, search, filtroEstado, filtroMes])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header con stats */}
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <div className="stats-inline">
          <span className="stat-num">{stats.total}</span>
          <span className="stat-label">gastos registrados</span>
          {stats.totalGastos > 0 && (
            <>
              <span className="stat-sep">·</span>
              <span className="stat-num">{fmtQ(stats.totalGastos)}</span>
              <span className="stat-label">acumulados</span>
            </>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCreating(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={14} weight="bold" />
          Registrar gasto
        </button>
      </div>

      {/* KPI cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        <KpiCard
          Icon={Receipt}
          label="Gastos este mes"
          value={String(stats.esteMes)}
          sub={fmtQ(stats.esteMesMonto)}
          tone="brand"
        />
        <KpiCard
          Icon={Money}
          label="Total acumulado"
          value={fmtQ(stats.totalGastos)}
          tone="neutral"
        />
        <KpiCard
          Icon={Clock}
          label="Pendientes de pago"
          value={String(stats.pendientes)}
          sub={fmtQ(stats.pendientesMonto)}
          tone={stats.pendientes > 0 ? 'warn' : 'neutral'}
        />
        <KpiCard
          Icon={ArrowDownRight}
          label="ISR retenido"
          value={fmtQ(stats.totalIsr)}
          sub="Por declarar a SAT"
          tone="info"
        />
      </div>

      {/* Filtros + búsqueda */}
      <div
        className="card"
        style={{
          padding: '12px 14px', display: 'flex', alignItems: 'center',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <div className="filters" style={{ display: 'flex', gap: 6 }}>
          {(['todos', 'pendientes', 'pagados'] as const).map((f) => (
            <button
              key={f}
              className={'chip' + (filtroEstado === f ? ' active' : '')}
              onClick={() => setFiltroEstado(f)}
              style={{ textTransform: 'capitalize' }}
            >
              {f}
            </button>
          ))}
        </div>
        {mesesDisponibles.length > 0 && (
          <select
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
            style={{ fontSize: 12, padding: '6px 10px' }}
          >
            <option value="todos">Todos los meses</option>
            {mesesDisponibles.map((m) => (
              <option key={m} value={m}>
                {new Date(m + '-01T00:00:00').toLocaleDateString('es-GT', { month: 'long', year: 'numeric' })}
              </option>
            ))}
          </select>
        )}
        <div className="search-input-wrap" style={{ flex: '0 1 320px', minWidth: 200 }}>
          <MagnifyingGlass size={14} weight="bold" className="search-icon" />
          <input
            placeholder="Buscar por proveedor, descripción, factura…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Lista */}
      <div className="card-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Proveedor / Descripción</th>
              <th>Categoría</th>
              <th>Factura</th>
              <th style={{ textAlign: 'right' }}>Monto</th>
              <th style={{ textAlign: 'right' }}>ISR</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30 }}>Cargando…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--muted-foreground)' }}>
                {rows.length === 0
                  ? <>Aún no hay gastos registrados. Comenzá con el botón <strong>Registrar gasto</strong>.</>
                  : <>Sin gastos que coincidan con los filtros.</>}
              </td></tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(r.fecha)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.proveedor_texto ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.descripcion}</div>
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {CATEGORIAS.find((c) => c.value === r.categoria)?.label ?? r.categoria}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                    {r.factura_serie || r.factura_numero ? `${r.factura_serie ?? ''}-${r.factura_numero ?? ''}` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtQ(r.monto_total)}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.isr_retenido > 0 ? 'var(--brand-600)' : 'var(--muted-foreground)' }}>
                    {r.isr_retenido > 0 ? fmtQ(r.isr_retenido) : '—'}
                  </td>
                  <td>
                    <span
                      className="chip"
                      style={r.estado === 'pagado'
                        ? { background: 'rgba(15,138,87,0.10)', color: '#0F8A57' }
                        : { background: 'rgba(212,160,23,0.10)', color: '#B8860B' }}
                    >
                      {r.estado === 'pagado' ? 'pagado' : 'pendiente'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {(r.factura_url || r.constancia_sat_url) && (
                      <Paperclip size={14} color="var(--muted-foreground)" weight="duotone" />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <RegistrarGastoModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); reload() }}
        />
      )}

      {selected && (
        <DetalleGastoDrawer
          gasto={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); reload() }}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Modal: registrar gasto
// -----------------------------------------------------------------------------

function RegistrarGastoModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [fecha, setFecha] = useState(hoyGT())
  const [categoria, setCategoria] = useState<CategoriaGasto>('PROFESIONALES')
  const [proveedores, setProveedores] = useState<Proveedor[]>([])
  const [proveedorId, setProveedorId] = useState<string>('')
  const [proveedorTexto, setProveedorTexto] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [montoTotalStr, setMontoTotalStr] = useState('')
  const [sujetoRetencion, setSujetoRetencion] = useState(true)
  const [formaPago, setFormaPago] = useState<FormaPago>('transferencia')
  const [bancos, setBancos] = useState<CuentaBancariaPropia[]>([])
  const [cuentaBancariaId, setCuentaBancariaId] = useState<string>('')
  const [facturaSerie, setFacturaSerie] = useState('')
  const [facturaNumero, setFacturaNumero] = useState('')
  const [notas, setNotas] = useState('')
  const [isrCalc, setIsrCalc] = useState<IsrCalc | null>(null)
  const [calculando, setCalculando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Cargar proveedores y bancos
  useEffect(() => {
    supabase.from('proveedor').select('*').eq('activo', true).order('nombre')
      .then(({ data }) => setProveedores((data ?? []) as Proveedor[]))
    supabase.from('cuenta_bancaria_propia').select('*').eq('activa', true).order('banco')
      .then(({ data }) => setBancos((data ?? []) as CuentaBancariaPropia[]))
  }, [])

  // Auto-seleccionar "sujeto a retención" según el régimen del proveedor
  // elegido. Para que el operador no tenga que pensar — si el proveedor
  // está bien clasificado en el catálogo, el form ya viene correcto.
  useEffect(() => {
    if (!proveedorId) return
    const p = proveedores.find((x) => x.id === proveedorId)
    if (!p) return
    if (p.regimen_tributario === 'DEFINITIVO') setSujetoRetencion(true)
    else if (p.regimen_tributario === 'PAGOS_TRIMESTRALES' || p.regimen_tributario === 'PEQUEÑO_CONTRIBUYENTE') {
      setSujetoRetencion(false)
    }
    // OTROS / null → no tocamos el checkbox (el operador decide)
  }, [proveedorId, proveedores])

  // Live calc de ISR con debounce
  const montoTotal = parseFloat(montoTotalStr.replace(/,/g, '')) || 0
  useEffect(() => {
    if (montoTotal <= 0) { setIsrCalc(null); return }
    setCalculando(true)
    const t = setTimeout(() => {
      supabase.rpc('calcular_isr_dual', {
        p_monto_total: montoTotal,
        p_sujeto_retencion: sujetoRetencion,
      }).then(({ data, error }) => {
        if (error) console.warn('calcular_isr_dual error', error)
        else if (data) setIsrCalc(data as IsrCalc)
        setCalculando(false)
      })
    }, 300)
    return () => { clearTimeout(t); setCalculando(false) }
  }, [montoTotal, sujetoRetencion])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!descripcion.trim()) { setError('La descripción es obligatoria.'); return }
    if (montoTotal <= 0) { setError('El monto debe ser mayor a cero.'); return }
    if (!proveedorId && !proveedorTexto.trim()) {
      setError('Indicá un proveedor (elegí de la lista o escribilo).'); return
    }
    const needsBanco = ['transferencia', 'cheque', 'deposito'].includes(formaPago)
    if (needsBanco && !cuentaBancariaId) {
      setError('Para pagos bancarios elegí la cuenta de origen.'); return
    }

    setSubmitting(true)
    const { error: rpcError } = await supabase.rpc('registrar_gasto', {
      p_input: {
        fecha,
        categoria,
        proveedor_id: proveedorId || null,
        proveedor_texto: proveedorId ? null : proveedorTexto.trim(),
        descripcion: descripcion.trim(),
        monto_neto: isrCalc?.monto_base ?? montoTotal,
        monto_total: montoTotal,
        isr_retenido: isrCalc?.isr_retenido ?? 0,
        forma_pago: formaPago === 'pendiente' ? null : formaPago,
        cuenta_bancaria_propia_id: needsBanco ? cuentaBancariaId : null,
        factura_serie: facturaSerie.trim() || null,
        factura_numero: facturaNumero.trim() || null,
        notas: notas.trim() || null,
      },
    })
    setSubmitting(false)
    if (rpcError) { setError(rpcError.message); return }
    onCreated()
  }

  return (
    <Modal open onClose={onClose} title="Registrar gasto operativo" size="lg">
      <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
        {/* Fila 1 — fecha + categoría */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Field label="Fecha" Icon={Calendar}>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </Field>
          <Field label="Categoría de gasto" Icon={Receipt}>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaGasto)}>
              {CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
        </div>

        {/* Proveedor */}
        <Field label="Proveedor" Icon={Buildings} hint="Seleccioná uno guardado o escribí uno nuevo">
          <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
            <option value="">— Proveedor nuevo / texto libre —</option>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}{p.nit ? ` · NIT ${p.nit}` : ''}
              </option>
            ))}
          </select>
          {!proveedorId && (
            <input
              placeholder="Nombre del proveedor (ej. Carlos Pérez · Honorarios contables)"
              value={proveedorTexto}
              onChange={(e) => setProveedorTexto(e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </Field>

        <Field label="Descripción del gasto">
          <input
            placeholder="Ej. Honorarios contabilidad · Mayo 2026"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            required
          />
        </Field>

        {/* Fila monto + sujeto retención */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Monto total de factura (con IVA si lo incluye)" Icon={Money}>
            <input
              inputMode="decimal"
              placeholder="0.00"
              value={montoTotalStr}
              onChange={(e) => setMontoTotalStr(e.target.value)}
              style={{ fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600 }}
            />
          </Field>
          <Field
            label="¿Sujeto a retención ISR?"
            hint='Marcar si la factura dice "SUJETO A RETENCIÓN DEFINITIVA"'
          >
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={sujetoRetencion}
                onChange={(e) => setSujetoRetencion(e.target.checked)}
              />
              <span>{sujetoRetencion ? 'Sí, retener ISR' : 'No retener (Pequeño Contribuyente / Régimen Utilidades)'}</span>
            </label>
          </Field>
        </div>

        {/* Calculadora ISR live */}
        {isrCalc && montoTotal > 0 && (
          <div
            style={{
              padding: 14,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--n-50)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Calculator size={16} weight="duotone" color="var(--brand)" />
              <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Cálculo de retención ISR {calculando ? '· calculando…' : ''}
              </strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 12 }}>
              <CalcRow label="Monto base (sin IVA)" value={fmtQ(isrCalc.monto_base)} sub="Total ÷ 1.12" />
              <CalcRow
                label="ISR a retener"
                value={fmtQ(isrCalc.isr_retenido)}
                sub={isrCalc.sujeto_retencion ? '5% hasta Q30k + 7% excedente' : 'No aplica'}
                highlight={isrCalc.isr_retenido > 0}
              />
              <CalcRow
                label="Monto a pagar al proveedor"
                value={fmtQ(isrCalc.monto_pagar)}
                sub="Total − ISR"
                color="var(--success)"
              />
            </div>
            {isrCalc.tramos && isrCalc.tramos.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)', fontSize: 11, color: 'var(--muted-foreground)' }}>
                Desglose:&nbsp;
                {isrCalc.tramos.map((t, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    <strong>{t.descripcion}</strong>: {fmtQ(t.monto)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Forma de pago */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Forma de pago" Icon={Bank}>
            <select value={formaPago} onChange={(e) => setFormaPago(e.target.value as FormaPago)}>
              {FORMAS_PAGO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
          {(['transferencia', 'cheque', 'deposito'] as FormaPago[]).includes(formaPago) && (
            <Field label="Cuenta bancaria de origen">
              <select value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value)} required>
                <option value="">— Seleccionar —</option>
                {bancos.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.banco} · {b.numero_cuenta} ({b.moneda})
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>

        {/* Factura */}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 2fr', gap: 12 }}>
          <Field label="Serie">
            <input placeholder="A" value={facturaSerie} onChange={(e) => setFacturaSerie(e.target.value)} />
          </Field>
          <Field label="Número de factura">
            <input placeholder="12345" value={facturaNumero} onChange={(e) => setFacturaNumero(e.target.value)} />
          </Field>
          <Field label="Notas internas">
            <input placeholder="Opcional" value={notas} onChange={(e) => setNotas(e.target.value)} />
          </Field>
        </div>

        {error && (
          <div className="error-banner" style={{ fontSize: 13 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting || montoTotal <= 0}>
            {submitting ? 'Registrando…' : `Registrar y generar asiento`}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// Componentes auxiliares
// -----------------------------------------------------------------------------

function Field({
  label, hint, Icon, children,
}: {
  label: string
  hint?: string
  Icon?: PhosphorIcon
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {Icon && <Icon size={12} weight="bold" />}
        {label}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{hint}</span>}
    </label>
  )
}

function KpiCard({
  Icon, label, value, sub, tone,
}: {
  Icon: PhosphorIcon
  label: string
  value: string
  sub?: string
  tone: 'neutral' | 'brand' | 'warn' | 'info'
}) {
  const colors: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: 'var(--n-100)', fg: 'var(--n-700)' },
    brand:   { bg: 'rgba(240,78,35,0.10)', fg: '#F04E23' },
    warn:    { bg: 'rgba(212,160,23,0.10)', fg: '#B8860B' },
    info:    { bg: 'rgba(37,99,235,0.08)', fg: '#2563EB' },
  }
  const c = colors[tone]
  return (
    <div className="kpi-card">
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 8, background: c.bg, color: c.fg, marginBottom: 8,
      }}>
        <Icon size={14} weight="duotone" />
      </div>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function CalcRow({
  label, value, sub, highlight, color,
}: {
  label: string
  value: string
  sub?: string
  highlight?: boolean
  color?: string
}) {
  return (
    <div style={{
      padding: '8px 10px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{ fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{
        fontSize: 14, fontWeight: 700,
        color: color ?? (highlight ? 'var(--brand-600)' : 'var(--ink)'),
        fontVariantNumeric: 'tabular-nums', marginTop: 2,
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: 'var(--muted-foreground)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Drawer detalle del gasto
// -----------------------------------------------------------------------------

type PartidaAsiento = {
  cuenta_codigo: string
  tipo: 'DEBE' | 'HABER'
  monto: number
  descripcion: string | null
}

function DetalleGastoDrawer({
  gasto,
  onClose,
  onUpdated,
}: {
  gasto: GastoOperativo
  onClose: () => void
  onUpdated: () => void
}) {
  const [partidas, setPartidas] = useState<PartidaAsiento[]>([])
  const [uploadingFactura, setUploadingFactura] = useState(false)
  const [uploadingConstancia, setUploadingConstancia] = useState(false)
  const [uploadingComprobante, setUploadingComprobante] = useState(false)

  useEffect(() => {
    if (!gasto.asiento_id) return
    supabase
      .from('partida_asiento')
      .select('cuenta_codigo, tipo, monto, descripcion')
      .eq('asiento_id', gasto.asiento_id)
      .order('orden')
      .then(({ data }) => setPartidas((data ?? []) as PartidaAsiento[]))
  }, [gasto.asiento_id])

  const subirArchivo = async (
    file: File,
    tipo: 'factura' | 'constancia' | 'comprobante',
    setLoading: (b: boolean) => void,
  ) => {
    // Validación de formato
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png']
    if (!validTypes.includes(file.type)) {
      alert('Formato no soportado. Usá PDF, JPG o PNG.')
      return
    }
    // Validación de tamaño (10 MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('Archivo demasiado grande. Máximo 10 MB.')
      return
    }

    setLoading(true)
    const ext = file.name.split('.').pop() ?? 'pdf'
    const path = `${gasto.id}/${tipo}-${Date.now()}.${ext}`
    const { error: upError } = await supabase.storage.from('gastos').upload(path, file, { upsert: true })
    if (upError) {
      alert('Error subiendo archivo: ' + upError.message)
      setLoading(false)
      return
    }
    const { data: urlData } = supabase.storage.from('gastos').getPublicUrl(path)
    const url = urlData.publicUrl
    const updateField =
      tipo === 'factura' ? { factura_url: url }
      : tipo === 'constancia' ? { constancia_sat_url: url }
      : { comprobante_url: url }
    const { error: updError } = await supabase
      .from('gasto_operativo')
      .update({ ...updateField, updated_at: new Date().toISOString() })
      .eq('id', gasto.id)
    if (updError) alert('Error guardando URL: ' + updError.message)
    setLoading(false)
    onUpdated()
  }

  const marcarPagado = async () => {
    if (!confirm('¿Marcar este gasto como pagado?')) return
    const { error } = await supabase
      .from('gasto_operativo')
      .update({ estado: 'pagado', updated_at: new Date().toISOString() })
      .eq('id', gasto.id)
    if (error) alert(error.message)
    else onUpdated()
  }

  return (
    <Modal open onClose={onClose} title="Detalle del gasto" size="lg">
      <div style={{ display: 'grid', gap: 18 }}>
        {/* Resumen */}
        <div style={{
          padding: 16, background: 'var(--n-50)',
          border: '1px solid var(--border)', borderRadius: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                {fmtDate(gasto.fecha)} · {CATEGORIAS.find((c) => c.value === gasto.categoria)?.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>
                {gasto.proveedor_texto ?? 'Sin proveedor'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 2 }}>
                {gasto.descripcion}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmtQ(gasto.monto_total)}
              </div>
              <span
                className="chip"
                style={gasto.estado === 'pagado'
                  ? { background: 'rgba(15,138,87,0.10)', color: '#0F8A57', marginTop: 6 }
                  : { background: 'rgba(212,160,23,0.10)', color: '#B8860B', marginTop: 6 }}
              >
                {gasto.estado === 'pagado' ? 'Pagado' : 'Pendiente de pago'}
              </span>
            </div>
          </div>
        </div>

        {/* Asiento contable generado */}
        {gasto.asiento_id && partidas.length > 0 && (
          <section>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>
              Asiento contable generado
            </h3>
            <div className="card-table">
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Cuenta</th>
                    <th>Descripción</th>
                    <th style={{ textAlign: 'right' }}>Debe</th>
                    <th style={{ textAlign: 'right' }}>Haber</th>
                  </tr>
                </thead>
                <tbody>
                  {partidas.map((p, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{p.cuenta_codigo}</td>
                      <td>{p.descripcion ?? '—'}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.tipo === 'DEBE' ? 'var(--ink)' : 'var(--muted-foreground)' }}>
                        {p.tipo === 'DEBE' ? fmtQ(p.monto) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.tipo === 'HABER' ? 'var(--ink)' : 'var(--muted-foreground)' }}>
                        {p.tipo === 'HABER' ? fmtQ(p.monto) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Comprobantes */}
        <section>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>
            Comprobantes
          </h3>

          {/* Warning si no hay ningún comprobante adjunto */}
          {!gasto.factura_url && !gasto.comprobante_url && !gasto.constancia_sat_url && (
            <div style={{
              padding: '8px 12px',
              background: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: 8,
              color: '#B45309',
              fontSize: 12,
              marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Warning size={14} weight="fill" />
              Sin comprobantes adjuntos. Recomendado subir al menos foto del recibo, factura o boleta.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <UploadSlot
              label="Factura del proveedor"
              url={gasto.factura_url}
              uploading={uploadingFactura}
              onChange={(f) => subirArchivo(f, 'factura', setUploadingFactura)}
              hint={gasto.factura_serie ? `Serie ${gasto.factura_serie} N° ${gasto.factura_numero}` : 'PDF, JPG o PNG'}
            />
            <UploadSlot
              label="Comprobante / recibo"
              url={gasto.comprobante_url}
              uploading={uploadingComprobante}
              onChange={(f) => subirArchivo(f, 'comprobante', setUploadingComprobante)}
              hint="Foto del recibo, boleta bancaria o similar (PDF, JPG, PNG)"
            />
            <UploadSlot
              label="Constancia retención SAT"
              url={gasto.constancia_sat_url}
              uploading={uploadingConstancia}
              onChange={(f) => subirArchivo(f, 'constancia', setUploadingConstancia)}
              hint={gasto.isr_retenido > 0 ? `ISR ${fmtQ(gasto.isr_retenido)}` : 'No aplica retención'}
              disabled={gasto.isr_retenido <= 0}
            />
          </div>
        </section>

        {gasto.notas && (
          <section>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>
              Notas internas
            </h3>
            <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: 0 }}>{gasto.notas}</p>
          </section>
        )}

        {/* Acciones */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
          {gasto.estado === 'registrado' && (
            <button className="btn btn-primary" onClick={marcarPagado}>
              <CheckCircle size={14} weight="bold" /> Marcar pagado
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

function UploadSlot({
  label,
  hint,
  url,
  uploading,
  onChange,
  disabled,
}: {
  label: string
  hint: string
  url: string | null
  uploading: boolean
  onChange: (file: File) => void
  disabled?: boolean
}) {
  const id = `upload-${label.replace(/\s/g, '-')}`
  return (
    <div style={{
      padding: 12,
      border: '1px dashed var(--border)',
      borderRadius: 8,
      background: disabled ? 'var(--n-50)' : 'var(--surface)',
      opacity: disabled ? 0.5 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FilePdf size={20} weight="duotone" color={url ? 'var(--brand-600)' : 'var(--n-400)'} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
          <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{hint}</div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
        {url ? (
          <>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-sm"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              <Eye size={12} /> Ver PDF
            </a>
            {!disabled && (
              <label htmlFor={id} className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                Reemplazar
              </label>
            )}
          </>
        ) : (
          <label
            htmlFor={id}
            className="btn btn-ghost btn-sm"
            style={{ flex: 1, justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {uploading ? 'Subiendo…' : disabled ? 'No aplica' : 'Subir archivo'}
          </label>
        )}
        <input
          id={id}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          disabled={disabled || uploading}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) onChange(f)
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
